// src/pages/BriefForm.jsx
//
// Universal brief-intake form with enhanced UX and premium visual design.
// Renders entirely from JSON schema from the backend.

import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Select } from "../arc/components/select/select";
import { Input } from "../arc/components/input/input";
import { Textarea } from "../arc/components/textarea/textarea";
import { Skeleton } from "../arc/components/skeleton/skeleton";
import {
  PhoneInput,
  PHONE_COUNTRIES,
} from "../arc/components/phone-input/phone-input";
import BriefUploadField from "../components/BriefUploadField";

// Mirrors normalizeUrlMentions() in services/briefFieldMap.js, kept as a
// small local copy rather than a cross-boundary import since this
// component is deliberately schema-driven with no backend imports. Strips
// a leading scheme/"www." from URL-looking tokens on blur so what the
// client sees while typing matches what the backend stores, without
// touching the rest of the line (the "- why" explanation).
const URL_TOKEN_RE =
  /\b(?:https?:\/\/)?(?:www\.)?([a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\/[^\s,]*)?)/gi;
function normalizeUrlMentions(value) {
  if (typeof value !== "string" || !value) return value;
  return value.replace(URL_TOKEN_RE, (_match, hostAndPath) => hostAndPath);
}

const PHONE_COUNTRY_CODES = [
  { code: "+44", label: "UK +44", country: "United Kingdom" },
  { code: "+353", label: "IE +353", country: "Ireland" },
  { code: "+1", label: "US +1", country: "United States" },
  { code: "+61", label: "AU +61", country: "Australia" },
  { code: "+33", label: "FR +33", country: "Other" },
  { code: "+49", label: "DE +49", country: "Other" },
  { code: "+34", label: "ES +34", country: "Other" },
  { code: "+91", label: "IN +91", country: "Other" },
  { code: "+27", label: "ZA +27", country: "South Africa" },
];

const COUNTRY_TO_DIAL_CODE = {
  "United Kingdom": "+44",
  Ireland: "+353",
  "United States": "+1",
  "South Africa": "+27",
  Australia: "+61",
};

const API_URL = process.env.REACT_APP_API_URL;
const publicClient = axios.create({ baseURL: API_URL, timeout: 30000 });

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

  /* :where() keeps the reset at zero specificity so component classes (Arc phone input, inputs, buttons)
     keep their own padding and margins instead of being flattened to 0. */
  :where(.brief-root, .brief-root *, .brief-root *::before, .brief-root *::after) { 
    box-sizing: border-box; margin: 0; padding: 0; 
  }

  .brief-root {
    --bg: #f6f5fb;
    --surface: #fffdfb;
    --surface-raised: #f9f7ff;
    --border: #e9e4f5;
    --border-strong: #cfc6e8;

    --text: #201a2e;
    --text-muted: #6e6580;
    --text-faint: #a49bb8;

    --accent: #4F46D5;
    --accent-dim: #eef0ff;
    --accent-hover: #3d35b8;
    --accent-subtle: #6f66e8;

    --pop: #ff8a5b;
    --pop-dim: #fff1e8;
    --pop-hover: #f2703c;
    --sun: #ffc94d;
    --sun-dim: #fff8e3;

    --green: #16a878;
    --green-dim: #e9faf3;
    --red: #e5484d;
    --red-dim: #fef0f0;

    --shadow-sm: 0 1px 2px rgba(79,70,213,0.06);
    --shadow-md: 0 6px 20px rgba(79,70,213,0.09);
    --shadow-lg: 0 14px 44px rgba(79,70,213,0.12);
    --shadow-xl: 0 24px 64px rgba(79,70,213,0.16);

    --radius: 14px;
    --radius-sm: 10px;
    --radius-lg: 20px;

    --sans: 'Instrument Sans', system-ui, -apple-system, sans-serif;
    --mono: 'Geist Mono', 'Fira Code', monospace;
    --transition: 0.18s cubic-bezier(0.22, 1, 0.36, 1);

    min-height: 100vh;
    background:
      radial-gradient(720px 420px at 92% -8%, var(--sun-dim), transparent 60%),
      radial-gradient(640px 480px at -6% 12%, var(--accent-dim), transparent 55%),
      var(--bg);
    color: var(--text);
    font-family: var(--sans);
    font-size: 14px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
  }

  .brief-header { 
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 14px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .brief-brand { 
    display: flex; 
    align-items: center; 
    gap: 10px; 
  }
  .brief-brand-mark { 
    width: 36px; 
    height: 36px; 
    border-radius: var(--radius-sm);
    background: var(--accent-dim);
    display: flex; 
    align-items: center; 
    justify-content: center; 
    overflow: hidden; 
    flex-shrink: 0;
  }
  .brief-brand-mark img { 
    width: 100%; 
    height: 100%; 
    object-fit: contain; 
    padding: 4px; 
  }
  .brief-brand-name { 
    font-size: 14px; 
    font-weight: 600; 
    color: var(--text); 
  }
  .brief-brand-sub { 
    font-family: var(--mono); 
    font-size: 8.5px; 
    letter-spacing: 0.1em; 
    text-transform: uppercase; 
    color: var(--text-faint); 
  }
  .brief-header-step { 
    font-family: var(--mono); 
    font-size: 10.5px; 
    letter-spacing: 0.05em; 
    text-transform: uppercase; 
    color: var(--text-faint); 
    flex-shrink: 0; 
  }
  .brief-header-step b { 
    color: var(--text); 
    font-weight: 600; 
  }

  .brief-progress-track { 
    height: 3px; 
    background: var(--border); 
    position: relative;
    overflow: hidden;
  }
  .brief-progress-fill { 
    height: 5px; 
    background: linear-gradient(90deg, var(--accent), var(--pop));
    transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    border-radius: 0 6px 6px 0;
    position: relative;
  }
  @keyframes progress-glow {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  .brief-progress-fill::after {
    content: "";
    position: absolute;
    top: 0; right: 0;
    width: 28px; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.7));
    animation: progress-glow 1.6s ease-in-out infinite;
  }

  .brief-char-budget { 
    max-width: 820px; 
    margin: 6px auto 0; 
    padding: 0 24px; 
    font-family: var(--mono); 
    font-size: 10.5px; 
    color: var(--text-faint); 
    text-align: right;
    letter-spacing: 0.02em;
  }
  .brief-char-budget.over { 
    color: var(--red); 
  }
  @media (max-width: 720px) { 
    .brief-char-budget { 
      padding: 0 16px; 
    } 
  }

  .brief-shell { 
    max-width: 820px; 
    margin: 0 auto; 
    padding: 28px 24px 80px; 
    display: grid; 
    grid-template-columns: 180px minmax(0, 1fr); 
    gap: 32px; 
    align-items: start; 
  }
  @media (max-width: 720px) { 
    .brief-shell { 
      grid-template-columns: 1fr; 
      padding: 20px 16px 60px; 
      gap: 20px;
    } 
  }

  .brief-nav { 
    position: sticky; 
    top: 20px; 
    display: flex; 
    flex-direction: column; 
    gap: 2px;
  }
  @media (max-width: 720px) { 
    .brief-nav { 
      position: static; 
      flex-direction: row; 
      overflow-x: auto; 
      gap: 4px; 
      padding-bottom: 4px;
    } 
  }
  .brief-nav-item { 
    display: flex; 
    align-items: center; 
    gap: 10px; 
    padding: 8px 12px; 
    border-radius: var(--radius-sm); 
    font-size: 12.5px; 
    font-weight: 450; 
    color: var(--text-faint); 
    cursor: default; 
    transition: all var(--transition);
  }
  .brief-nav-item.done { 
    color: var(--text-muted); 
    cursor: pointer; 
  }
  .brief-nav-item.done:hover {
    background: var(--surface);
  }
  .brief-nav-item.active { 
    background: var(--accent-dim);
    color: var(--accent); 
    font-weight: 600;
    transform: translateX(2px);
  }
  @media (max-width: 720px) {
    .brief-nav-item { 
      white-space: nowrap; 
      padding: 6px 14px;
      font-size: 12px;
    }
  }
  .brief-nav-dot { 
    width: 21px; 
    height: 21px; 
    border-radius: 50%; 
    border: 1.5px solid var(--border-strong); 
    display: flex; 
    align-items: center; 
    justify-content: center; 
    font-family: var(--mono); 
    font-size: 9px; 
    font-weight: 600; 
    color: var(--text-faint); 
    flex-shrink: 0;
    transition: all var(--transition);
    background: var(--surface);
  }
  .brief-nav-item.active .brief-nav-dot { 
    border-color: var(--accent); 
    color: #fff; 
    background: var(--accent);
    box-shadow: 0 0 0 4px var(--accent-dim);
  }
  .brief-nav-item.done .brief-nav-dot { 
    border-color: var(--green); 
    background: var(--green-dim); 
    color: var(--green); 
    animation: dot-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .brief-nav-item.done:hover .brief-nav-dot {
    transform: scale(1.12) rotate(-6deg);
  }
  @keyframes dot-pop {
    0% { transform: scale(0.6); }
    100% { transform: scale(1); }
  }

  .brief-card { 
    background: var(--surface); 
    border: 1px solid var(--border); 
    border-radius: var(--radius-lg); 
    padding: 32px 32px 28px; 
    box-shadow: var(--shadow-sm);
    transition: opacity 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease;
  }
  .brief-card.entering {
    opacity: 0;
    transform: translateY(6px);
  }
  @keyframes field-in {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  /* backwards, not both: a held end-frame transform would make every field its own stacking context
     and the next field would paint over an open dropdown (phone country list). */
  .field-group {
    animation: field-in 0.3s ease backwards;
  }
  .field-group:nth-of-type(1) { animation-delay: 0.02s; }
  .field-group:nth-of-type(2) { animation-delay: 0.06s; }
  .field-group:nth-of-type(3) { animation-delay: 0.1s; }
  .field-group:nth-of-type(4) { animation-delay: 0.14s; }
  .field-group:nth-of-type(5) { animation-delay: 0.18s; }
  @media (max-width: 480px) { 
    .brief-card { 
      padding: 24px 18px 20px; 
    } 
  }

  .brief-eyebrow { 
    font-family: var(--mono); 
    font-size: 9.5px; 
    font-weight: 500; 
    letter-spacing: 0.08em; 
    text-transform: uppercase; 
    color: var(--text-faint); 
    margin-bottom: 4px; 
  }
  .brief-title { 
    font-size: 20px; 
    font-weight: 600; 
    letter-spacing: -0.01em; 
    color: var(--text); 
    margin-bottom: 4px; 
  }
  .brief-subtitle { 
    font-size: 13px; 
    color: var(--text-muted); 
    margin-bottom: 24px; 
  }

  .field-group { 
    margin-bottom: 20px; 
  }
  .field-label, .field-group label { 
    display: flex; 
    align-items: baseline; 
    gap: 4px; 
    font-size: 12.5px; 
    font-weight: 500; 
    color: var(--text); 
    margin-bottom: 6px; 
  }
  .field-required { 
    color: var(--red); 
    font-size: 11px; 
  }
  .field-optional-badge {
    display: inline-block;
    background: var(--sun-dim);
    border: 1px solid #f3dfa0;
    border-radius: 100px;
    padding: 0 9px;
    font-size: 9px;
    font-weight: 600;
    color: #93670c;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-left: 6px;
    line-height: 17px;
  }
  .field-input, .field-textarea, .field-select {
    width: 100%; 
    padding: 9px 12px; 
    background: var(--surface-raised); 
    border: 1px solid var(--border);
    border-radius: var(--radius-sm); 
    font-family: var(--sans); 
    font-size: 13.5px; 
    color: var(--text);
    transition: border var(--transition), box-shadow var(--transition);
    outline: none;
  }
  .field-input::placeholder, .field-textarea::placeholder { 
    color: var(--text-faint); 
  }
  .field-input, .field-textarea, .field-select {
    transition: border-color var(--transition), box-shadow var(--transition), background var(--transition), transform 0.1s ease;
  }
  .field-input:focus, .field-textarea:focus, .field-select:focus { 
    background: var(--surface); 
    border-color: var(--accent); 
    box-shadow: 0 0 0 3px var(--accent-dim); 
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-4px); }
    40% { transform: translateX(4px); }
    60% { transform: translateX(-3px); }
    80% { transform: translateX(3px); }
  }
  .field-input.has-error, .field-textarea.has-error, .field-select.has-error {
    animation: shake 0.32s ease;
  }
  .field-textarea { 
    resize: vertical; 
    min-height: 80px; 
  }
  .field-select { 
    appearance: none; 
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%236b7280' stroke-width='1.5'><path d='M5 8l5 5 5-5'/></svg>"); 
    background-repeat: no-repeat; 
    background-position: right 10px center; 
    padding-right: 34px; 
  }

  .chip-row { 
    display: flex; 
    flex-wrap: wrap; 
    gap: 6px; 
  }
  .chip { 
    padding: 7px 16px; 
    border-radius: 100px; 
    border: 1.5px solid var(--border); 
    background: var(--surface-raised); 
    color: var(--text-muted); 
    font-family: var(--sans); 
    font-size: 12.5px; 
    font-weight: 500; 
    cursor: pointer; 
    transition: all var(--transition);
  }
  .chip:hover { 
    border-color: var(--pop); 
    color: var(--pop-hover);
    transform: translateY(-1px);
  }
  .chip.selected { 
    background: var(--accent); 
    border-color: var(--accent); 
    color: #fff;
    animation: chip-pop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .field-slider { padding: 6px 2px; }
  .field-range { width: 100%; accent-color: var(--accent); cursor: pointer; }
  .field-slider-endpoints { 
    display: flex; 
    justify-content: space-between; 
    font-family: var(--sans); 
    font-size: 11.5px; 
    color: var(--text-muted); 
    margin-top: 4px; 
  }
  .chip.selected:hover {
    background: var(--accent-hover);
  }
  @keyframes chip-pop {
    0% { transform: scale(0.92); }
    100% { transform: scale(1); }
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 36px;
    padding: 0 16px;
    border: 1px solid transparent;
    border-radius: 8px;
    font-family: var(--sans);
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1.45;
    cursor: pointer;
    white-space: nowrap;
    text-decoration: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    transition: color var(--transition), background var(--transition), border-color var(--transition), box-shadow var(--transition), opacity var(--transition);
  }
  .btn:focus-visible {
    outline: 2px solid var(--accent-mid);
    outline-offset: 2px;
  }
  .btn:disabled {
    opacity: 0.52;
    cursor: not-allowed;
  }
  .btn-sm { min-height: 32px; padding: 0 12px; }
  .btn-md { min-height: 36px; padding: 0 16px; }
  .btn-lg { min-height: 40px; padding: 0 20px; }
  .btn:active:not(:disabled) {
    transform: translateY(1px) scale(0.97);
  }
  .btn-primary { 
    background: linear-gradient(135deg, var(--accent), var(--accent-hover)); 
    color: #fff; 
    box-shadow: 0 2px 8px rgba(79,70,213,0.3);
  }
  .btn-primary:hover:not(:disabled) { 
    box-shadow: 0 8px 20px rgba(79,70,213,0.35);
    transform: translateY(-2px);
  }
  .btn-primary:disabled { 
    opacity: 0.5; 
    cursor: default; 
  }
  .btn-ghost { 
    background: transparent; 
    border: 1.5px solid var(--border); 
    color: var(--text-muted); 
  }
  .btn-ghost:hover:not(:disabled) { 
    background: var(--surface-raised); 
    border-color: var(--accent-subtle); 
    color: var(--accent); 
  }
  .btn-ghost:disabled { 
    opacity: 0.3; 
    cursor: default; 
  }
  .btn-remove {
    padding: 3px 12px;
    font-size: 11px;
    color: var(--text-muted);
    background: transparent;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: all var(--transition);
    font-family: var(--sans);
    font-weight: 450;
  }
  .btn-remove:hover {
    border-color: var(--red);
    color: var(--red);
    background: var(--red-dim);
  }

  .brief-footer-nav { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    margin-top: 28px; 
    padding-top: 18px; 
    border-top: 1px solid var(--border); 
  }

  .brief-centered { 
    display: flex; 
    min-height: 60vh; 
    align-items: center; 
    justify-content: center; 
    padding: 24px; 
    text-align: center; 
  }
  .brief-status-card { 
    max-width: 360px;
  }
  .brief-status-icon { 
    width: 56px; 
    height: 56px; 
    border-radius: 50%; 
    display: flex; 
    align-items: center; 
    justify-content: center; 
    margin: 0 auto 18px; 
    font-size: 22px;
    font-weight: 600;
    position: relative;
    animation: status-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes status-pop {
    0% { transform: scale(0.4); opacity: 0; }
    60% { transform: scale(1.08); opacity: 1; }
    100% { transform: scale(1); }
  }
  .brief-status-icon.tone-green {
    box-shadow: 0 0 0 8px var(--green-dim);
  }
  .brief-confetti {
    position: absolute;
    inset: -30px;
    pointer-events: none;
  }
  .brief-confetti span {
    position: absolute;
    top: 50%; left: 50%;
    width: 6px; height: 6px;
    border-radius: 1px;
    background: var(--accent);
    animation: confetti-burst 0.8s ease-out forwards;
  }
  .brief-confetti span:nth-child(1) { background: var(--accent); transform: rotate(0deg); animation-delay: 0.05s; }
  .brief-confetti span:nth-child(2) { background: var(--green); transform: rotate(45deg); animation-delay: 0.1s; }
  .brief-confetti span:nth-child(3) { background: var(--accent-subtle); transform: rotate(90deg); animation-delay: 0.02s; }
  .brief-confetti span:nth-child(4) { background: var(--green); transform: rotate(135deg); animation-delay: 0.12s; }
  .brief-confetti span:nth-child(5) { background: var(--accent); transform: rotate(180deg); animation-delay: 0.06s; }
  .brief-confetti span:nth-child(6) { background: var(--accent-subtle); transform: rotate(225deg); animation-delay: 0.15s; }
  .brief-confetti span:nth-child(7) { background: var(--green); transform: rotate(270deg); animation-delay: 0.08s; }
  .brief-confetti span:nth-child(8) { background: var(--accent); transform: rotate(315deg); animation-delay: 0.03s; }
  @keyframes confetti-burst {
    0% { transform: translate(-50%, -50%) rotate(var(--r, 0deg)) translateY(0); opacity: 1; }
    100% { transform: translate(-50%, -50%) rotate(var(--r, 0deg)) translateY(-46px); opacity: 0; }
  }
  .brief-status-title { 
    font-size: 18px; 
    font-weight: 600; 
    color: var(--text); 
    margin-bottom: 6px; 
    animation: field-in-status 0.4s ease 0.1s both;
  }
  .brief-status-sub { 
    font-size: 13.5px; 
    color: var(--text-muted); 
    animation: field-in-status 0.4s ease 0.18s both;
  }
  @keyframes field-in-status {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .skeleton-shimmer { 
    background: linear-gradient(90deg, var(--surface-raised) 25%, var(--border) 50%, var(--surface-raised) 75%); 
    background-size: 200% 100%; 
    animation: shimmer 1.4s infinite; 
    border-radius: var(--radius-sm); 
  }
  @keyframes shimmer { 
    0% { background-position: 200% 0; } 
    100% { background-position: -200% 0; } 
  }

  .repeat-card { 
    border: 1px solid var(--border); 
    border-radius: var(--radius); 
    padding: 18px; 
    margin-bottom: 12px; 
    background: var(--surface-raised); 
  }
  .repeat-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .repeat-card-title { 
    font-family: var(--mono); 
    font-size: 10px; 
    font-weight: 500; 
    letter-spacing: 0.06em; 
    text-transform: uppercase; 
    color: var(--text-muted); 
  }
  .repeat-sub-label { 
    display: block; 
    font-size: 11.5px; 
    font-weight: 450; 
    color: var(--text-muted); 
    margin-bottom: 4px; 
  }

  .btn-dashed { 
    width: 100%; 
    padding: 10px; 
    border-radius: var(--radius-sm); 
    border: 1.5px dashed var(--border-strong); 
    background: transparent; 
    color: var(--text-muted); 
    font-family: var(--sans); 
    font-size: 12.5px; 
    font-weight: 450; 
    cursor: pointer; 
    transition: all var(--transition); 
  }
  .btn-dashed:hover { 
    border-color: var(--accent); 
    color: var(--accent); 
    background: var(--accent-dim); 
  }

  .logo-upload { 
    margin-top: 18px; 
    padding-top: 18px; 
    border-top: 1px solid var(--border); 
  }
  .logo-upload .brief-eyebrow { 
    margin-bottom: 10px; 
  }
  .logo-drop {
    display: grid;
    grid-template-columns: 56px 1fr auto;
    align-items: center;
    gap: 16px;
    padding: 16px 18px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface-raised);
    cursor: pointer;
    transition: all var(--transition);
  }
  .logo-drop:hover, .logo-drop.dragover {
    border-color: var(--accent);
    background: var(--accent-dim);
  }
  .logo-drop:not(.has-file) {
    grid-template-columns: 56px 1fr;
  }
  .logo-drop input[type="file"] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .logo-preview {
    width: 56px;
    height: 56px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    background: var(--surface);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
  }
  .logo-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 6px;
  }
  .logo-drop-icon {
    width: 56px;
    height: 56px;
    border-radius: var(--radius-sm);
    border: 1px dashed var(--border-strong);
    background: var(--surface);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--text-faint);
    font-size: 18px;
  }
  .logo-drop-text {
    min-width: 0;
  }
  .logo-drop-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .logo-drop-sub {
    font-family: var(--mono);
    font-size: 10px;
    color: var(--text-faint);
    margin-top: 3px;
  }
  .logo-remove {
    flex-shrink: 0;
    justify-self: end;
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-muted);
    border-radius: var(--radius-sm);
    font-size: 11px;
    padding: 6px 14px;
    cursor: pointer;
    font-family: var(--sans);
    font-weight: 450;
    white-space: nowrap;
    transition: all var(--transition);
  }
  .logo-remove:hover {
    border-color: var(--red);
    color: var(--red);
    background: var(--red-dim);
  }
  .logo-error {
    font-size: 11.5px;
    color: var(--red);
    margin-top: 6px;
  }
  @media (max-width: 480px) {
    .logo-drop {
      grid-template-columns: 44px 1fr;
      grid-template-areas:
        "preview text"
        "remove remove";
      row-gap: 12px;
    }
    .logo-preview, .logo-drop-icon { grid-area: preview; width: 44px; height: 44px; }
    .logo-drop-text { grid-area: text; }
    .logo-remove { grid-area: remove; justify-self: stretch; text-align: center; }
  }

  .gallery-upload { 
    margin-top: 18px; 
    padding-top: 18px; 
    border-top: 1px solid var(--border); 
  }
  .gallery-upload .brief-eyebrow { 
    margin-bottom: 8px; 
  }
  .gallery-add {
    display: flex; 
    align-items: center; 
    justify-content: center; 
    gap: 6px; 
    padding: 12px;
    border: 1.5px dashed var(--border-strong); 
    border-radius: var(--radius); 
    background: var(--surface-raised);
    color: var(--text-muted); 
    font-size: 12.5px; 
    font-weight: 450; 
    cursor: pointer; 
    transition: all var(--transition);
  }
  .gallery-add:hover { 
    border-color: var(--accent); 
    color: var(--accent); 
    background: var(--accent-dim); 
  }
  .gallery-add input[type="file"] { 
    display: none; 
  }
  .gallery-grid { 
    display: grid; 
    grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); 
    gap: 10px; 
    margin-bottom: 10px; 
  }
  .gallery-thumb { 
    position: relative; 
    aspect-ratio: 1; 
    border: 1px solid var(--border); 
    border-radius: var(--radius-sm); 
    overflow: hidden; 
    background: var(--surface);
    transition: all var(--transition);
  }
  .gallery-thumb:hover {
    border-color: var(--border-strong);
  }
  .gallery-thumb-remove { 
    position: absolute; 
    top: 4px; 
    right: 4px; 
    width: 20px; 
    height: 20px; 
    border-radius: 50%; 
    border: none; 
    background: rgba(0,0,0,0.5); 
    color: #fff; 
    font-size: 12px; 
    line-height: 1; 
    cursor: pointer; 
    display: flex; 
    align-items: center; 
    justify-content: center;
    transition: background var(--transition);
  }
  .gallery-thumb-remove:hover { 
    background: var(--red);
  }
  .gallery-count { 
    font-family: var(--mono); 
    font-size: 10px; 
    color: var(--text-faint); 
    margin-top: 4px; 
  }

  .field-count { 
    font-family: var(--mono); 
    font-size: 10px; 
    color: var(--text-faint); 
    margin-top: 4px; 
    text-align: right;
  }
  .field-help { 
    font-size: 11.5px; 
    color: var(--text-muted); 
    margin-top: 4px; 
    line-height: 1.4; 
  }
  .field-error { 
    font-size: 11.5px; 
    color: var(--red); 
    margin-top: 4px; 
  }
  .field-input.has-error, .field-textarea.has-error, .field-select.has-error { 
    border-color: var(--red); 
    background: var(--red-dim); 
  }

  /* PhoneInput wears the same skin as .field-input (it reads these variables). */
  .field-group .phone-field {
    --pi-h: 40px;
    --pi-bg: var(--surface-raised);
    --pi-bg-focus: var(--surface);
    --pi-bg-invalid: var(--red-dim);
    --pi-border: var(--border);
    --pi-border-focus: var(--accent);
    --pi-radius: var(--radius-sm);
    --pi-font: 13.5px;
    --pi-ring: 0 0 0 3px var(--accent-dim);
    --pi-shape-r: 5px;
    --pi-shape-open-r: 8px;
    --pi-option-r: 6px;
    --pi-bg-open: var(--surface);
  }
  .phone-field [data-invalid] { animation: shake 0.32s ease; }

  .contact-block { 
    margin-top: 4px; 
    padding-top: 18px; 
    border-top: 1px solid var(--border); 
  }
  .contact-block .brief-eyebrow { 
    margin-bottom: 10px; 
  }
  .contact-grid { 
    display: grid; 
    grid-template-columns: 1fr 1fr; 
    gap: 14px; 
  }
  @media (max-width: 480px) { 
    .contact-grid { 
      grid-template-columns: 1fr; 
      gap: 12px;
    } 
  }

  .repeat-checkbox { 
    width: 16px; 
    height: 16px; 
    accent-color: var(--accent); 
    cursor: pointer; 
  }

  .field-color-row { 
    display: flex; 
    align-items: center; 
    gap: 10px; 
  }
  .field-color-swatch-wrap { 
    position: relative; 
    width: 36px; 
    height: 36px; 
    border-radius: var(--radius-sm); 
    border: 1px solid var(--border); 
    overflow: hidden; 
    flex-shrink: 0; 
    cursor: pointer; 
    transition: border var(--transition); 
  }
  .field-color-swatch-wrap:hover { 
    border-color: var(--border-strong); 
  }
  .field-color-swatch-wrap:focus-within { 
    border-color: var(--accent); 
    box-shadow: 0 0 0 3px var(--accent-dim); 
  }
  .field-color-swatch { 
    position: absolute; 
    top: -3px; 
    left: -3px; 
    width: calc(100% + 6px); 
    height: calc(100% + 6px); 
    border: none; 
    padding: 0; 
    cursor: pointer; 
    background: none; 
  }
  .field-color-swatch::-webkit-color-swatch-wrapper { 
    padding: 0; 
  }
  .field-color-swatch::-webkit-color-swatch { 
    border: none; 
  }
  .field-hex-wrap { 
    display: flex; 
    align-items: center; 
    height: 36px; 
    border: 1px solid var(--border); 
    border-radius: var(--radius-sm); 
    background: var(--surface-raised); 
    padding: 0 10px 0 4px; 
    transition: border var(--transition), box-shadow var(--transition); 
  }
  .field-hex-wrap:focus-within { 
    background: var(--surface); 
    border-color: var(--accent); 
    box-shadow: 0 0 0 3px var(--accent-dim); 
  }
  .field-hex-wrap.invalid { 
    border-color: var(--red); 
  }
  .field-hex-prefix { 
    font-family: var(--mono); 
    font-size: 12px; 
    color: var(--text-faint); 
    padding: 0 2px 0 6px; 
    user-select: none; 
  }
  .field-hex-input { 
    width: 80px; 
    border: none; 
    background: transparent; 
    outline: none; 
    font-family: var(--mono); 
    font-size: 12.5px; 
    letter-spacing: 0.03em; 
    color: var(--text); 
    text-transform: uppercase; 
    padding: 0; 
  }
  .field-hex-input::placeholder { 
    color: var(--text-faint); 
    text-transform: none; 
  }

  /* Form notice banner, appears once at the top of the form */
  .brief-notice {
    background: linear-gradient(135deg, var(--accent-dim), var(--sun-dim));
    border: 1.5px solid var(--accent-subtle);
    border-radius: var(--radius);
    padding: 14px 18px;
    margin-bottom: 24px;
    font-size: 13px;
    color: var(--text);
    line-height: 1.5;
  }
  .brief-notice strong {
    color: var(--accent);
  }
  .brief-notice .optional-highlight {
    display: inline-block;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 100px;
    padding: 0 8px;
    font-size: 10px;
    font-weight: 500;
    color: var(--text-faint);
    letter-spacing: 0.03em;
    line-height: 16px;
    margin: 0 2px;
  }

  /* "Other" text input that appears below select when Other is chosen */
  .other-input-wrap {
    margin-top: 8px;
  }
  .other-input-wrap .field-input {
    background: var(--surface);
  }

  @media (max-width: 720px) {
    .brief-nav::-webkit-scrollbar {
      height: 2px;
    }
    .brief-nav::-webkit-scrollbar-thumb {
      background: var(--border-strong);
      border-radius: 2px;
    }
  }
`;

export default function BriefForm() {
  const { token } = useParams();
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [services, setServices] = useState([]);
  const [competitorUrls, setCompetitorUrls] = useState([]);
  const formRef = useRef(null);

  const TOTAL_CHARACTER_BUDGET = 5000;

  const totalCharsUsed = useMemo(() => {
    let total = 0;
    for (const v of Object.values(answers)) {
      if (typeof v === "string") total += v.length;
      else if (Array.isArray(v)) {
        total += v.reduce(
          (sum, item) => sum + (typeof item === "string" ? item.length : 0),
          0,
        );
      }
    }
    for (const service of services) {
      for (const v of Object.values(service || {})) {
        if (typeof v === "string") total += v.length;
      }
    }
    for (const url of competitorUrls) {
      if (typeof url === "string") total += url.length;
    }
    return total;
  }, [answers, services, competitorUrls]);
  const overBudget = totalCharsUsed > TOTAL_CHARACTER_BUDGET;
  const [contact, setContact] = useState({
    phone: "",
    email: "",
    phoneCountryCode: "+44",
    phoneCountry: "GB",
  });
  const [logo, setLogo] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [brandGuidelines, setBrandGuidelines] = useState([]);
  const [status, setStatus] = useState("loading");
  const [activeSection, setActiveSection] = useState(0);
  const [errors, setErrors] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);

  // ── Universal link: no company baked into the token, so the client picks
  // or types theirs as a step that gates the rest of the wizard. Kept
  // outside `sections`/`activeSection` on purpose, it isn't part of the
  // schema, it's a one-off step this page injects in front of it.
  const [isUniversal, setIsUniversal] = useState(false);
  const [companyConfirmed, setCompanyConfirmed] = useState(false);
  const [companyMode, setCompanyMode] = useState("search"); // "search" | "manual"
  const [chQuery, setChQuery] = useState("");
  const [chResults, setChResults] = useState([]);
  const [chLoading, setChLoading] = useState(false);
  const [chError, setChError] = useState("");
  const [companyDetails, setCompanyDetails] = useState({
    companyNumber: "",
    companyName: "",
    addressLine1: "",
    locality: "",
    postcode: "",
    sicCode: "",
    country: "United Kingdom",
    countryOther: "",
  });
  const [companyError, setCompanyError] = useState("");

  // Keep the phone country code in sync with the selected company country,
  // but only while the user hasn't started entering a number themselves.
  useEffect(() => {
    const dial = COUNTRY_TO_DIAL_CODE[companyDetails.country];
    if (dial) {
      const entry =
        PHONE_COUNTRIES.find((c) => c.name === companyDetails.country) ??
        PHONE_COUNTRIES.find((c) => `+${c.dial}` === dial);
      setContact((prev) =>
        prev.phone
          ? prev
          : {
              ...prev,
              phoneCountryCode: dial,
              ...(entry ? { phoneCountry: entry.iso } : {}),
            },
      );
    }
  }, [companyDetails.country]);

  const MAX_LOGO_BYTES = 5 * 1024 * 1024;
  const MAX_GALLERY_IMAGES = 9;
  const MAX_BRAND_GUIDELINE_FILES = 5;
  const MAX_BRAND_GUIDELINE_BYTES = 10 * 1024 * 1024;
  const LOGO_ACCEPT = "image/png,image/jpeg,image/svg+xml,image/webp";
  const GALLERY_ACCEPT = "image/png,image/jpeg,image/webp";
  const BRAND_ASSET_ACCEPT =
    "image/png,image/jpeg,image/webp,application/pdf,.doc,.docx,.ppt,.pptx,.zip,.ttf,.otf";

  useEffect(() => {
    async function init() {
      try {
        const [schemaRes, linkRes] = await Promise.all([
          publicClient.get("/briefs/form-schema").then((r) => r.data),
          publicClient.get(`/briefs/form-link/${token}`).then((r) => r.data),
        ]);
        if (!linkRes.valid) {
          setStatus("invalid");
          return;
        }
        setSchema(schemaRes.schema);
        setIsUniversal(!!linkRes.universal);

        // Pre-fill whatever we already know about this company (trading
        // name, primary location/address, contact phone/email) so the
        // client isn't retyping details we collected earlier in the
        // funnel, e.g. via the Wizzyl partner order that created this
        // company, or the company record itself. Only relevant for a
        // per-company link; a universal link has no company yet, so
        // linkRes.prefill comes back empty and these are no-ops. Runs once
        // before the client has typed anything, so it's safe to merge in
        // wholesale, later edits go through setAnswer/setContact as normal.
        const prefill = linkRes.prefill;
        if (prefill?.answers && Object.keys(prefill.answers).length) {
          setAnswers((prev) => ({ ...prev, ...prefill.answers }));
        }
        if (
          prefill?.contact &&
          (prefill.contact.phone || prefill.contact.email)
        ) {
          setContact((prev) => ({
            ...prev,
            phone: prefill.contact.phone || prev.phone,
            email: prefill.contact.email || prev.email,
          }));
        }

        setStatus("ready");
      } catch (err) {
        setStatus("error");
      }
    }
    init();
  }, [token]);

  // ── Companies House search (debounced), hits the existing public
  // /api/companies/search endpoint, same one the internal dashboard uses.
  useEffect(() => {
    if (companyMode !== "search" || chQuery.trim().length < 2) {
      setChResults([]);
      return;
    }
    let cancelled = false;
    setChLoading(true);
    setChError("");
    const handle = setTimeout(async () => {
      try {
        const { data } = await publicClient.get("/companies/search", {
          params: { q: chQuery.trim() },
        });
        if (!cancelled) setChResults(data.success ? data.data || [] : []);
      } catch (err) {
        if (!cancelled)
          setChError("Couldn't search Companies House right now.");
      } finally {
        if (!cancelled) setChLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [chQuery, companyMode]);

  // ── Enhanced: auto-populate relevant form fields from company data ──
  function autoPopulateFromCompany(result) {
    console.log("Auto-populating from:", result.companyName);

    // Use the exact company name from Companies House - don't clean it
    const exactCompanyName = result.companyName || "";

    // Build a clean version for fields that need it (like USP, description)
    const cleanName =
      result.companyName
        ?.replace(/\s+(LIMITED|LTD|PLC|LLP|UK|GB|ENGLAND|WALES)\b/gi, "")
        .trim() ||
      result.companyName ||
      "";

    // Extract industry from SIC codes
    let industryHint = "";
    let industryCategory = "Other";

    if (result.sicCodes && result.sicCodes.length > 0) {
      // Map common SIC codes to industry descriptions
      const sicMap = {
        62020: "Information technology consultancy",
        62012: "Business and domestic software development",
        62090: "Other information technology service activities",
        63110: "Data processing, hosting and related activities",
        70229:
          "Management consultancy activities (other than financial management)",
        82990: "Other business support service activities n.e.c.",
        41100: "Development of building projects",
        41202: "Construction of domestic buildings",
        43210: "Electrical installation",
        43390: "Other building completion and finishing",
        45111: "Sale of new cars and light motor vehicles",
        45112: "Sale of used cars and light motor vehicles",
        45200: "Maintenance and repair of motor vehicles",
        47110:
          "Retail sale in non-specialised stores with food, beverages or tobacco predominating",
        47190: "Other retail sale in non-specialised stores",
        47910: "Retail sale via mail order houses or via Internet",
        56101: "Licensed restaurants",
        56102: "Unlicensed restaurants and cafes",
        56103: "Take-away food shops and mobile food stands",
        68209: "Other letting and operating of own or leased real estate",
        86900: "Other human health activities",
        96090: "Other service activities n.e.c.",
      };
      const firstSic = String(result.sicCodes[0]);
      industryHint = sicMap[firstSic] || `SIC: ${firstSic}`;
    }

    // Map to clean industry category
    const industryMap = {
      "Information technology": "Tech / Software",
      "Software development": "Tech / Software",
      "Management consultancy": "Consulting",
      Construction: "Construction",
      Building: "Construction",
      Electrical: "Construction",
      "Motor vehicles": "Automotive",
      Retail: "Retail",
      Restaurants: "Hospitality",
      Cafes: "Hospitality",
      "Real estate": "Property",
      Health: "Healthcare",
      "Business support": "Professional Services",
      Consultancy: "Consulting",
    };

    for (const [key, value] of Object.entries(industryMap)) {
      if (industryHint.toLowerCase().includes(key.toLowerCase())) {
        industryCategory = value;
        break;
      }
    }

    // Get location details
    const location = result.address?.locality || "";
    const postcode = result.address?.postalCode || "";
    const addressLine1 = result.address?.line1 || "";

    // ── Build auto-populated answers by finding the right field names ──
    const autoAnswers = {};

    console.log(
      "Schema sections:",
      schema?.sections?.map((s) => s.title),
    );

    // First, try to find the correct field names from the schema
    if (schema && schema.sections) {
      for (const section of schema.sections) {
        if (section.fields) {
          for (const field of section.fields) {
            const name = field.name;
            const label = field.label?.toLowerCase() || "";

            console.log(`Field: name="${name}", label="${label}"`);

            // Trading name / business name - USE EXACT COMPANY NAME
            if (
              label.includes("trading") ||
              (label.includes("business") && label.includes("name")) ||
              name?.toLowerCase().includes("trading") ||
              name?.toLowerCase().includes("business_name")
            ) {
              // Use the exact company name from Companies House
              if (exactCompanyName) autoAnswers[name] = exactCompanyName;
              console.log(
                `  → Setting trading name: "${exactCompanyName}" to field "${name}"`,
              );
            }
            // Company name - USE EXACT COMPANY NAME
            else if (
              (label.includes("company") && label.includes("name")) ||
              name?.toLowerCase().includes("company_name")
            ) {
              if (exactCompanyName) autoAnswers[name] = exactCompanyName;
              console.log(
                `  → Setting company name: "${exactCompanyName}" to field "${name}"`,
              );
            }
            // Brand name - USE EXACT COMPANY NAME
            else if (
              (label.includes("brand") && label.includes("name")) ||
              name?.toLowerCase().includes("brand_name")
            ) {
              if (exactCompanyName) autoAnswers[name] = exactCompanyName;
              console.log(
                `  → Setting brand name: "${exactCompanyName}" to field "${name}"`,
              );
            }
            // Industry / Sector
            else if (
              label.includes("industry") ||
              label.includes("sector") ||
              name?.toLowerCase().includes("industry") ||
              name?.toLowerCase().includes("sector")
            ) {
              if (industryCategory) autoAnswers[name] = industryCategory;
              console.log(
                `  → Setting industry: "${industryCategory}" to field "${name}"`,
              );
            }
            // Industry description / detail
            else if (
              (label.includes("industry") &&
                (label.includes("detail") ||
                  label.includes("description") ||
                  label.includes("type"))) ||
              name?.toLowerCase().includes("industry_description")
            ) {
              if (industryHint) autoAnswers[name] = industryHint;
              console.log(
                `  → Setting industry description: "${industryHint}" to field "${name}"`,
              );
            }
            // Location / City / Town
            else if (
              label.includes("city") ||
              label.includes("town") ||
              label.includes("location") ||
              name?.toLowerCase().includes("city") ||
              name?.toLowerCase().includes("town") ||
              name?.toLowerCase().includes("location")
            ) {
              if (location) autoAnswers[name] = location;
              console.log(
                `  → Setting location: "${location}" to field "${name}"`,
              );
            }
            // Area
            else if (
              label.includes("area") ||
              name?.toLowerCase().includes("area")
            ) {
              if (location) autoAnswers[name] = location;
              console.log(`  → Setting area: "${location}" to field "${name}"`);
            }
            // Postcode
            else if (
              label.includes("postcode") ||
              label.includes("postal") ||
              name?.toLowerCase().includes("postcode") ||
              name?.toLowerCase().includes("postal")
            ) {
              if (postcode) autoAnswers[name] = postcode;
              console.log(
                `  → Setting postcode: "${postcode}" to field "${name}"`,
              );
            }
            // Address
            else if (
              (label.includes("address") && label.includes("line")) ||
              name?.toLowerCase().includes("address_line")
            ) {
              if (addressLine1) autoAnswers[name] = addressLine1;
              console.log(
                `  → Setting address: "${addressLine1}" to field "${name}"`,
              );
            }
            // Description / About - use exact company name in description
            else if (
              label.includes("description") ||
              label.includes("about") ||
              label.includes("overview") ||
              name?.toLowerCase().includes("description") ||
              name?.toLowerCase().includes("about")
            ) {
              const description = [
                exactCompanyName || result.companyName,
                industryHint ? `(${industryHint})` : "",
                location ? `based in ${location}` : "",
              ]
                .filter(Boolean)
                .join(" ");
              if (description.length > 20) {
                autoAnswers[name] = description;
                console.log(
                  `  → Setting description: "${description}" to field "${name}"`,
                );
              }
            }
            // Target audience
            else if (
              (label.includes("target") && label.includes("audience")) ||
              (label.includes("ideal") && label.includes("customer")) ||
              name?.toLowerCase().includes("target_audience")
            ) {
              if (industryHint || location) {
                const audience = [
                  location ? `${location} businesses` : "",
                  industryHint ? `in the ${industryHint} sector` : "",
                  "seeking digital growth",
                ]
                  .filter(Boolean)
                  .join(" ");
                if (audience.length > 10) {
                  autoAnswers[name] = audience;
                  console.log(
                    `  → Setting target audience: "${audience}" to field "${name}"`,
                  );
                }
              }
            }
            // USP / Unique selling point - use clean name for USP
            else if (
              label.includes("usp") ||
              label.includes("unique") ||
              label.includes("selling") ||
              name?.toLowerCase().includes("usp") ||
              name?.toLowerCase().includes("unique")
            ) {
              if (cleanName && (industryHint || location)) {
                const usp = `${cleanName}, ${industryHint || "business services"} ${location ? `in ${location}` : ""}`;
                autoAnswers[name] = usp;
                console.log(`  → Setting USP: "${usp}" to field "${name}"`);
              }
            }
          }
        }
      }
    }

    // If no schema fields were found, use fallback field names
    if (Object.keys(autoAnswers).length === 0) {
      console.log("No schema fields found, using fallback field names");

      // Fallback: try common field names
      const fallbackFields = {
        // Trading name gets the EXACT company name
        trading_name: exactCompanyName || result.companyName,
        business_name: exactCompanyName || result.companyName,
        // Company name gets the EXACT company name
        company_name: exactCompanyName || result.companyName,
        brand_name: exactCompanyName || result.companyName,
        industry: industryCategory,
        sector: industryCategory,
        industry_description: industryHint,
        location: location,
        city: location,
        town: location,
        area: location,
        postcode: postcode,
        address_line1: addressLine1,
        company_description: `${exactCompanyName || result.companyName} ${industryHint ? `(${industryHint})` : ""} ${location ? `based in ${location}` : ""}`,
        target_audience: `${location ? `${location} businesses` : ""} ${industryHint ? `in the ${industryHint} sector` : ""} seeking digital growth`,
        // USP uses the clean name
        usp: `${cleanName}, ${industryHint || "business services"} ${location ? `in ${location}` : ""}`,
      };

      // Only set non-empty values
      for (const [key, value] of Object.entries(fallbackFields)) {
        if (value && String(value).trim()) {
          autoAnswers[key] = value;
        }
      }

      console.log("Fallback answers:", autoAnswers);
    }

    console.log("Final auto-populated answers:", autoAnswers);

    // ── Set all auto-populated values ──
    setAnswers((prev) => ({
      ...prev,
      ...autoAnswers,
    }));

    // ── Services ──
    const suggestedServices = [];
    const serviceMap = {
      "Information technology": [
        "IT Consulting",
        "Software Development",
        "Cloud Services",
        "Cybersecurity",
      ],
      Consulting: [
        "Strategy Consulting",
        "Business Advisory",
        "Digital Transformation",
        "Change Management",
      ],
      Construction: [
        "Project Management",
        "Design & Build",
        "Renovation Services",
        "Construction Management",
      ],
      Automotive: [
        "Vehicle Sales",
        "Servicing & Maintenance",
        "Parts Supply",
        "Fleet Management",
      ],
      Retail: [
        "E-commerce Solutions",
        "Inventory Management",
        "Customer Experience",
        "Point of Sale Systems",
      ],
      Hospitality: [
        "Restaurant Management",
        "Catering Services",
        "Event Hosting",
        "Customer Experience Design",
      ],
      Property: [
        "Property Management",
        "Real Estate Advisory",
        "Leasing Services",
        "Property Development",
      ],
      Healthcare: [
        "Patient Care Services",
        "Healthcare Management",
        "Wellness Programs",
        "Medical Consulting",
      ],
      "Tech / Software": [
        "Web Development",
        "Mobile Applications",
        "Cloud Infrastructure",
        "Digital Transformation",
      ],
      "Professional Services": [
        "Business Consulting",
        "Project Management",
        "Strategic Planning",
        "Operational Excellence",
      ],
    };

    let matchedIndustry = "Other";
    for (const [key, value] of Object.entries(serviceMap)) {
      if (
        (industryHint &&
          industryHint.toLowerCase().includes(key.toLowerCase())) ||
        (cleanName && cleanName.toLowerCase().includes(key.toLowerCase()))
      ) {
        matchedIndustry = key;
        break;
      }
    }

    const servicesList = serviceMap[matchedIndustry] || [];

    servicesList.slice(0, 3).forEach((name) => {
      const serviceObj = {
        name: name,
        description: `${name} for ${cleanName || "clients"}${location ? ` in ${location}` : ""}`,
      };
      suggestedServices.push(serviceObj);
    });

    if (suggestedServices.length > 0) {
      console.log("Suggested services:", suggestedServices);
      setServices(suggestedServices);
    }

    // ── Competitors ──
    const competitorSuggestions = [];
    if (industryHint && location) {
      competitorSuggestions.push(
        `https://www.google.com/search?q=${encodeURIComponent(`${industryHint} ${location}`)}`,
        `https://www.google.com/search?q=${encodeURIComponent(`${cleanName || "business"} competitors`)}`,
      );
    }

    if (competitorSuggestions.length > 0) {
      console.log("Suggested competitors:", competitorSuggestions);
      setCompetitorUrls(competitorSuggestions);
    }

    // ── Company details ──
    setCompanyDetails({
      companyNumber: result.companyNumber || "",
      // Use the exact company name from Companies House
      companyName: exactCompanyName || result.companyName || "",
      addressLine1: result.address?.line1 || "",
      locality: result.address?.locality || "",
      postcode: result.address?.postalCode || "",
      sicCode: (result.sicCodes || [])[0] || "",
    });

    setChResults([]);
    setChQuery(result.companyName || "");

    console.log("Auto-population complete!");
  }

  function selectChResult(result) {
    autoPopulateFromCompany(result);
  }

  function confirmCompany() {
    if (!companyDetails.companyName || !companyDetails.companyName.trim()) {
      setCompanyError("Please enter or select your company name.");
      return;
    }
    if (
      companyDetails.country === "Other" &&
      !companyDetails.countryOther?.trim()
    ) {
      setCompanyError("Please enter your country.");
      return;
    }
    setCompanyError("");
    setCompanyConfirmed(true);

    // If we have company details but no auto-populated answers yet,
    // try to auto-populate from the company details
    if (companyDetails.companyName && Object.keys(answers).length === 0) {
      // Create a result object from companyDetails
      const result = {
        companyNumber: companyDetails.companyNumber,
        companyName: companyDetails.companyName,
        address: {
          line1: companyDetails.addressLine1,
          locality: companyDetails.locality,
          postalCode: companyDetails.postcode,
        },
        sicCodes: companyDetails.sicCode ? [companyDetails.sicCode] : [],
      };
      autoPopulateFromCompany(result);
    }
  }

  function setAnswer(name, value) {
    setAnswers((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function clearError(key) {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  function isValidPhone(value) {
    return /^[+\d][\d\s().-]{6,}$/.test(value.trim());
  }

  function isValidUrl(value) {
    try {
      const u = new URL(value.trim());
      return !!u.protocol.match(/^https?:$/);
    } catch {
      return false;
    }
  }

  function validateSection(index) {
    const sections = schema?.sections;
    if (!sections) return false;

    const sec = sections[index];
    const newErrors = {};

    sec.fields?.forEach((field) => {
      const val = answers[field.name];
      if (field.required) {
        if (field.type === "multiselect") {
          if (!Array.isArray(val) || val.length === 0) {
            newErrors[field.name] = "Please select at least one option.";
          }
        } else if (field.type === "file") {
          if (!val) newErrors[field.name] = "Please upload a file.";
        } else if (
          val === undefined ||
          val === null ||
          String(val).trim() === ""
        ) {
          newErrors[field.name] = "This field is required.";
        }
      }
      if (field.type === "url" && val && !isValidUrl(String(val))) {
        newErrors[field.name] =
          "Please enter a valid URL (e.g. https://example.com).";
      }
      if (field.type === "color" && val && !HEX_RE.test(String(val))) {
        newErrors[field.name] =
          "Please enter a valid hex colour, e.g. #0B2B66.";
      }
      if (
        field.type === "number" &&
        val !== undefined &&
        val !== null &&
        val !== "" &&
        isNaN(Number(val))
      ) {
        newErrors[field.name] = "Please enter a valid number.";
      }
    });

    if (sec.repeatGroup) {
      services.forEach((service, i) => {
        sec.repeatGroup.fields.forEach((f) => {
          const key = f.key.toLowerCase();
          if (f.required && f.type !== "checkbox" && !service[key]) {
            newErrors[`service_${i}_${key}`] = `${f.label} is required.`;
          }
        });
      });
    }

    if (sec.competitorUrls) {
      competitorUrls.forEach((url, i) => {
        if (url && !isValidUrl(url)) {
          newErrors[`competitor_${i}`] = "Please enter a valid URL.";
        }
      });
    }

    if (sec.id === "business_information") {
      if (!contact.phone || !contact.phone.trim()) {
        newErrors.contact_phone = "Phone number is required.";
      } else if (!isValidPhone(contact.phone)) {
        newErrors.contact_phone = "Please enter a valid phone number.";
      }
      if (!contact.email || !contact.email.trim()) {
        newErrors.contact_email = "Email address is required.";
      } else if (!isValidEmail(contact.email)) {
        newErrors.contact_email = "Please enter a valid email address.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function toggleMultiselect(name, option, max) {
    setAnswers((prev) => {
      const current = Array.isArray(prev[name]) ? prev[name] : [];
      const has = current.includes(option);
      let next;
      if (has) {
        next = current.filter((o) => o !== option);
      } else {
        if (max && current.length >= max) return prev;
        next = [...current, option];
      }
      return { ...prev, [name]: next };
    });
  }

  function updateService(index, key, value) {
    setServices((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), [key]: value };
      return next;
    });
    clearError(`service_${index}_${key}`);
  }

  function addService() {
    setServices((prev) => [...prev, {}]);
  }

  function removeService(index) {
    setServices((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`service_${index}_`)) {
          delete next[key];
        }
      });
      return next;
    });
  }

  function updateCompetitor(index, value) {
    setCompetitorUrls((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    clearError(`competitor_${index}`);
  }

  async function handleSubmit() {
    if (!validateSection(activeSection)) return;
    if (overBudget) return;
    setStatus("saving");
    try {
      const { phoneCountry: _phoneCountry, ...contactFields } = contact;
      const payload = {
        answers: { ...answers, services, competitorUrls },
        contact: {
          ...contactFields,
          // PhoneInput stores the number in E.164 (+44…), so it is sent as-is.
          phone: contact.phone,
        },
        ...(isUniversal ? { companyDetails } : {}),
      };
      let data;
      if (logo || gallery.length || brandGuidelines.length) {
        const form = new FormData();
        form.append("payload", JSON.stringify(payload));
        if (logo) form.append("logo", logo);
        gallery.forEach((file) => form.append("gallery", file));
        brandGuidelines.forEach((file) => form.append("brandGuidelines", file));
        data = await publicClient
          .post(`/briefs/form-submit/${token}`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((r) => r.data);
      } else {
        data = await publicClient
          .post(`/briefs/form-submit/${token}`, payload)
          .then((r) => r.data);
      }
      setStatus(data.success ? "done" : "error");
    } catch (err) {
      setStatus("error");
    }
  }

  if (status === "loading") {
    return (
      <div className="brief-root">
        <style>{styles}</style>
        <div className="brief-centered">
          <div style={{ width: 240 }}>
            <Skeleton lines={2} label="Loading form" />
          </div>
        </div>
      </div>
    );
  }

  if (status === "invalid")
    return (
      <StatusScreen
        icon="!"
        tone="amber"
        title="Link expired"
        sub="Ask your account manager to send a fresh link."
      />
    );
  if (status === "error")
    return (
      <StatusScreen
        icon="!"
        tone="red"
        title="Something went wrong"
        sub="Please refresh or contact your account manager."
      />
    );
  if (status === "done")
    return (
      <StatusScreen
        icon="✓"
        tone="green"
        title="Brief submitted"
        sub="Thanks, we'll be in touch shortly."
      />
    );

  // ── Universal link gate ──
  if (isUniversal && !companyConfirmed) {
    return (
      <div className="brief-root">
        <style>{styles}</style>
        <header className="brief-header">
          <div className="brief-brand">
            <div className="brief-brand-mark">
              <img
                src="https://irp.cdn-website.com/0e528108/dms3rep/multi/Wizzyl.png"
                alt="Wizzyl"
              />
            </div>
            <div>
              <div className="brief-brand-name">Wizzyl</div>
              <div className="brief-brand-sub">Client brief</div>
            </div>
          </div>
          <div className="brief-header-step">
            <b>0</b> / {(schema?.sections?.length || 0) + 1}
          </div>
        </header>
        <div className="brief-progress-track">
          <div className="brief-progress-fill" style={{ width: "6%" }} />
        </div>

        <div className="brief-shell" style={{ gridTemplateColumns: "1fr" }}>
          <div className="brief-card" ref={formRef}>
            <div className="brief-eyebrow">Get started</div>
            <h2 style={{ fontSize: 20, fontWeight: 600, margin: "4px 0 6px" }}>
              Your company
            </h2>
            <p
              style={{
                color: "var(--text-muted)",
                marginBottom: 20,
                fontSize: 13,
              }}
            >
              Tell us who this brief is for. UK companies can be found by search
             , otherwise enter the details by hand.
            </p>

            <div className="chip-row" style={{ marginBottom: 18 }}>
              <button
                type="button"
                className={`chip ${companyMode === "search" ? "selected" : ""}`}
                onClick={() => setCompanyMode("search")}
              >
                Search Companies House
              </button>
              <button
                type="button"
                className={`chip ${companyMode === "manual" ? "selected" : ""}`}
                onClick={() => setCompanyMode("manual")}
              >
                Enter details manually
              </button>
            </div>

            {companyMode === "search" && (
              <div className="field-group">
                <label className="field-label">Company name</label>
                <Input
                  label="Company name"
                  hideLabel
                  type="text"
                  className="field-input"
                  placeholder="Start typing your company name…"
                  value={chQuery}
                  onChange={(e) => setChQuery(e.target.value)}
                  autoComplete="off"
                />
                {chLoading && (
                  <div className="field-help">Searching Companies House…</div>
                )}
                {chError && <div className="field-error">{chError}</div>}
                {chResults.length > 0 && (
                  <div
                    className="repeat-card"
                    style={{ padding: 6, marginTop: 8 }}
                  >
                    {chResults.map((r) => (
                      <button
                        type="button"
                        key={r.companyNumber}
                        onClick={() => selectChResult(r)}
                        className="btn-dashed"
                        style={{
                          width: "100%",
                          textAlign: "left",
                          display: "block",
                          marginTop: 0,
                          marginBottom: 4,
                        }}
                      >
                        <strong style={{ fontWeight: 600 }}>
                          {r.companyName}
                        </strong>
                        <div
                          style={{
                            fontFamily: "var(--mono)",
                            fontSize: 11,
                            color: "var(--text-faint)",
                            marginTop: 2,
                          }}
                        >
                          {r.companyNumber}
                          {r.address?.locality
                            ? ` · ${r.address.locality}`
                            : ""}
                        </div>
                        {r.sicCodes && r.sicCodes.length > 0 && (
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--text-muted)",
                              marginTop: 2,
                            }}
                          >
                            SIC: {r.sicCodes.join(", ")}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {chQuery.trim().length >= 2 &&
                  !chLoading &&
                  chResults.length === 0 && (
                    <div className="field-help">
                      Can't find it? Switch to "Enter details manually".
                    </div>
                  )}
                {companyDetails.companyName && (
                  <div
                    className="repeat-card"
                    style={{ marginTop: 12, background: "var(--accent-dim)" }}
                  >
                    <div
                      className="brief-eyebrow"
                      style={{ color: "var(--accent)" }}
                    >
                      Selected
                    </div>
                    <strong>{companyDetails.companyName}</strong>
                    {companyDetails.companyNumber && (
                      <div
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: 11,
                          color: "var(--text-muted)",
                          marginTop: 2,
                        }}
                      >
                        {companyDetails.companyNumber}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-muted)",
                        marginTop: 4,
                      }}
                    >
                      ✓ Auto-populated business details, services, and more
                    </div>
                  </div>
                )}
              </div>
            )}

            {companyMode === "manual" && (
              <>
                <div className="field-group">
                  <label className="field-label">
                    Company name <span className="field-required">*</span>
                  </label>
                  <Input
                    label="Company name"
                    hideLabel
                    type="text"
                    className={`field-input ${companyError ? "has-error" : ""}`}
                    placeholder="Your company name"
                    value={companyDetails.companyName}
                    onChange={(e) =>
                      setCompanyDetails((p) => ({
                        ...p,
                        companyName: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="contact-grid">
                  <div className="field-group">
                    <Select
                      label="Country"
                      className="field-input"
                      value={companyDetails.country}
                      onValueChange={(value) =>
                        setCompanyDetails((p) => ({
                          ...p,
                          country: value,
                          ...(value !== "United Kingdom"
                            ? { companyNumber: "" }
                            : {}),
                        }))
                      }
                      options={[
                        { value: "United Kingdom", label: "United Kingdom" },
                        { value: "Ireland", label: "Ireland" },
                        { value: "United States", label: "United States" },
                        { value: "South Africa", label: "South Africa" },
                        { value: "Australia", label: "Australia" },
                        { value: "Other", label: "Other" },
                      ]}
                    />
                    {companyDetails.country === "Other" && (
                      <Input
                        label="Country"
                        hideLabel
                        type="text"
                        className="field-input"
                        style={{ marginTop: 8 }}
                        placeholder="Enter country name"
                        value={companyDetails.countryOther || ""}
                        onChange={(e) =>
                          setCompanyDetails((p) => ({
                            ...p,
                            countryOther: e.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                  <div className="field-group">
                    <label className="field-label">
                      Company number{" "}
                      {companyDetails.country === "United Kingdom" && (
                        <span style={{ color: "var(--text-faint)" }}>
                          (optional)
                        </span>
                      )}
                    </label>
                    <Input
                      label="Company number"
                      hideLabel
                      type="text"
                      className="field-input"
                      placeholder={
                        companyDetails.country === "United Kingdom"
                          ? "e.g. 12345678"
                          : "N/A"
                      }
                      value={companyDetails.companyNumber}
                      disabled={companyDetails.country !== "United Kingdom"}
                      onChange={(e) =>
                        setCompanyDetails((p) => ({
                          ...p,
                          companyNumber: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="field-group">
                  <label className="field-label">Address line 1</label>
                  <Input
                    label="Address line 1"
                    hideLabel
                    type="text"
                    className="field-input"
                    placeholder="Street address"
                    value={companyDetails.addressLine1}
                    onChange={(e) =>
                      setCompanyDetails((p) => ({
                        ...p,
                        addressLine1: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="contact-grid">
                  <div className="field-group">
                    <label className="field-label">Town / city</label>
                    <Input
                      label="Town / city"
                      hideLabel
                      type="text"
                      className="field-input"
                      value={companyDetails.locality}
                      onChange={(e) =>
                        setCompanyDetails((p) => ({
                          ...p,
                          locality: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Postcode</label>
                    <Input
                      label="Postcode"
                      hideLabel
                      type="text"
                      className="field-input"
                      value={companyDetails.postcode}
                      onChange={(e) =>
                        setCompanyDetails((p) => ({
                          ...p,
                          postcode: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {companyError && (
              <div
                className="field-error"
                style={{ marginTop: 4, marginBottom: 8 }}
              >
                {companyError}
              </div>
            )}

            <div
              className="brief-footer-nav"
              style={{ justifyContent: "flex-end" }}
            >
              <button
                type="button"
                onClick={confirmCompany}
                className="btn btn-primary"
              >
                Continue →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sections = schema.sections;
  const section = sections[activeSection];
  const pct = ((activeSection + 1) / sections.length) * 100;
  const isFirst = activeSection === 0;
  const isLastSection = activeSection === sections.length - 1;
  const progressNote = isLastSection
    ? "Almost there, last step"
    : isFirst
      ? `${totalCharsUsed.toLocaleString()} / ${TOTAL_CHARACTER_BUDGET.toLocaleString()} characters`
      : `${sections.length - activeSection - 1} step${sections.length - activeSection - 1 === 1 ? "" : "s"} to go · ${totalCharsUsed.toLocaleString()}/${TOTAL_CHARACTER_BUDGET.toLocaleString()} chars`;

  const handleNext = () => {
    if (validateSection(activeSection)) {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveSection((i) => Math.min(sections.length - 1, i + 1));
        setIsTransitioning(false);
        if (formRef.current) {
          formRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 200);
    }
  };

  const handleBack = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveSection((i) => Math.max(0, i - 1));
      setIsTransitioning(false);
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 200);
  };

  return (
    <div className="brief-root" ref={formRef}>
      <style>{styles}</style>

      <header className="brief-header">
        <div className="brief-brand">
          <div className="brief-brand-mark">
            <img
              src="https://irp.cdn-website.com/0e528108/dms3rep/multi/Wizzyl.png"
              alt="Wizzyl"
            />
          </div>
          <div>
            <div className="brief-brand-name">Wizzyl</div>
            <div className="brief-brand-sub">Client brief</div>
          </div>
        </div>
        <div className="brief-header-step">
          <b>{activeSection + 1}</b> / {sections.length}
        </div>
      </header>
      <div className="brief-progress-track">
        <div className="brief-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className={`brief-char-budget ${overBudget ? "over" : ""}`}>
        {overBudget
          ? `${totalCharsUsed.toLocaleString()} / ${TOTAL_CHARACTER_BUDGET.toLocaleString()} characters, over limit`
          : progressNote}
      </div>

      <div className="brief-shell">
        <nav className="brief-nav">
          {sections.map((s, i) => (
            <div
              key={s.id || i}
              className={`brief-nav-item ${i === activeSection ? "active" : ""} ${i < activeSection ? "done" : ""}`}
              onClick={() => i < activeSection && setActiveSection(i)}
            >
              <span className="brief-nav-dot">
                {i < activeSection ? "✓" : i + 1}
              </span>
              <span>{s.title}</span>
            </div>
          ))}
        </nav>

        <div
          className={`brief-card ${isTransitioning ? "entering" : ""}`}
          key={activeSection}
        >
          {/* ── Form notice ── shows once at the top of the first section ── */}
          {activeSection === 0 && schema.formNotice && (
            <div className="brief-notice">{schema.formNotice}</div>
          )}

          <div className="brief-eyebrow">
            Section {activeSection + 1} of {sections.length}
          </div>
          <div className="brief-title">{section.title}</div>
          {section.description && (
            <div className="brief-subtitle">{section.description}</div>
          )}

          {section.fields?.map((field) => (
            <Field
              key={field.name}
              field={field}
              value={answers[field.name]}
              error={errors[field.name]}
              onChange={(v) => setAnswer(field.name, v)}
              onToggle={(opt) =>
                toggleMultiselect(field.name, opt, field.maxSelections)
              }
            />
          ))}

          {section.repeatGroup && (
            <ServiceRepeater
              group={section.repeatGroup}
              services={services}
              errors={errors}
              onUpdate={updateService}
              onAdd={addService}
              onRemove={removeService}
            />
          )}

          {section.competitorUrls && (
            <CompetitorRepeater
              max={section.competitorUrls.max}
              values={competitorUrls}
              errors={errors}
              onUpdate={updateCompetitor}
            />
          )}

          {section.id === "look_feel" && (
            <BriefUploadField
              eyebrow="Brand assets"
              initialFiles={brandGuidelines}
              label="Add brand files"
              description={`PDF, Word, PowerPoint, images, zip or fonts · up to 10MB each · max ${MAX_BRAND_GUIDELINE_FILES}`}
              accept={BRAND_ASSET_ACCEPT}
              maxSize={MAX_BRAND_GUIDELINE_BYTES}
              maxFiles={MAX_BRAND_GUIDELINE_FILES}
              noun="files"
              onFilesChange={setBrandGuidelines}
            />
          )}

          {section.id === "business_information" && (
            <>
              <ContactFields
                contact={contact}
                errors={errors}
                companyCountry={companyDetails.country}
                onChange={(next) => {
                  setContact(next);
                  clearError("contact_phone");
                  clearError("contact_email");
                }}
              />
              <BriefUploadField
                className="logo-upload"
                eyebrow="Brand logo"
                initialFiles={logo ? [logo] : []}
                label="Drop your logo here, or click to browse"
                description="PNG, JPG, SVG or WEBP · up to 5MB"
                accept={LOGO_ACCEPT}
                maxSize={MAX_LOGO_BYTES}
                multiple={false}
                onFilesChange={(files) => setLogo(files[0] ?? null)}
              />
              <BriefUploadField
                eyebrow="Gallery"
                initialFiles={gallery}
                label="Add photos"
                description={`PNG, JPG or WEBP · up to 5MB each · max ${MAX_GALLERY_IMAGES}`}
                accept={GALLERY_ACCEPT}
                maxSize={MAX_LOGO_BYTES}
                maxFiles={MAX_GALLERY_IMAGES}
                noun="photos"
                onFilesChange={setGallery}
              />
            </>
          )}

          <NavButtons
            activeSection={activeSection}
            total={sections.length}
            onBack={handleBack}
            onNext={handleNext}
            onSubmit={handleSubmit}
            saving={status === "saving"}
            overBudget={overBudget}
          />
        </div>
      </div>
    </div>
  );
}

function StatusScreen({ icon, tone, title, sub }) {
  const toneMap = {
    green: { bg: "var(--green-dim)", color: "var(--green)" },
    red: { bg: "var(--red-dim)", color: "var(--red)" },
    amber: { bg: "#fffbeb", color: "#d97706" },
  };
  const t = toneMap[tone] || toneMap.green;
  return (
    <div className="brief-root">
      <style>{styles}</style>
      <div className="brief-centered">
        <div className="brief-status-card">
          <div
            className={`brief-status-icon ${tone === "green" ? "tone-green" : ""}`}
            style={{ background: t.bg, color: t.color }}
          >
            {icon}
            {tone === "green" && (
              <span className="brief-confetti" aria-hidden="true">
                {Array.from({ length: 8 }).map((_, i) => (
                  <span key={i} />
                ))}
              </span>
            )}
          </div>
          <div className="brief-status-title">{title}</div>
          <div className="brief-status-sub">{sub}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ field, value, error, onChange, onToggle }) {
  const { type, label, options = [], maxLength, required } = field;

  // ── "Other" select support ─────────────────────────────────────────────────
  const [otherValue, setOtherValue] = useState("");
  const [isOtherSelected, setIsOtherSelected] = useState(false);

  // If the current value matches the otherTrigger, show the other input
  const allowOther = field.allowOther === true;
  const otherTrigger = field.otherTrigger || "Other";

  // When the field value changes, check if it's the "Other" trigger
  const handleSelectChange = (selectedValue) => {
    if (allowOther && selectedValue === otherTrigger) {
      setIsOtherSelected(true);
      // Don't set the answer yet, wait for the text input
      onChange("");
    } else {
      setIsOtherSelected(false);
      onChange(selectedValue);
    }
  };

  // When the other text input changes, update the answer with the custom value
  const handleOtherChange = (text) => {
    setOtherValue(text);
    onChange(text);
  };

  // If the field value is not the trigger but we're in "Other" mode, reset
  // This can happen if the user selects Other, types, then selects something else
  useEffect(() => {
    if (isOtherSelected && value !== otherTrigger && value !== otherValue) {
      // If the value is something else (not the trigger and not our other text)
      // we might need to reset state
      if (value && value !== otherValue) {
        setIsOtherSelected(false);
        setOtherValue("");
      }
    }
    // If the value is the trigger but we're not in other mode, enter it
    if (allowOther && value === otherTrigger && !isOtherSelected) {
      setIsOtherSelected(true);
    }
  }, [value, isOtherSelected, otherTrigger, otherValue, allowOther]);

  const placeholder = field.placeholder || `Enter ${label.toLowerCase()}`;

  return (
    <div className="field-group">
      <label className="field-label">
        {label}
        {required ? (
          <span className="field-required">*</span>
        ) : (
          <span className="field-optional-badge">Optional</span>
        )}
      </label>

      {type === "text" && (
        <Input
          label={label}
          hideLabel
          type="text"
          className={`field-input ${error ? "has-error" : ""}`}
          placeholder={placeholder}
          value={value || ""}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {type === "textarea" && (
        <Textarea
          label={label}
          hideLabel
          className={`field-textarea ${error ? "has-error" : ""}`}
          placeholder={placeholder}
          value={value || ""}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
          onBlur={
            field.enforceUrlFormat
              ? (e) => onChange(normalizeUrlMentions(e.target.value))
              : undefined
          }
          rows={4}
        />
      )}

      {type === "number" && (
        <Input
          label={label}
          hideLabel
          type="number"
          className={`field-input ${error ? "has-error" : ""}`}
          placeholder={field.placeholder || "0"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {type === "url" && (
        <Input
          label={label}
          hideLabel
          type="url"
          className={`field-input ${error ? "has-error" : ""}`}
          placeholder={field.placeholder || "https://example.com"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {type === "color" && (
        <ColorField
          value={value}
          onChange={onChange}
          defaultValue={field.default || "#0b2b66"}
        />
      )}

      {type === "file" && (
        <input
          type="file"
          className={`field-input ${error ? "has-error" : ""}`}
          accept={field.accept || "image/*"}
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      )}

      {type === "select" && (
        <>
          <Select
            label={label}
            hideLabel
            className={`field-input ${error ? "has-error" : ""}`}
            placeholder={field.placeholder || "Select…"}
            value={isOtherSelected ? otherTrigger : value || ""}
            onValueChange={handleSelectChange}
            options={options.map((opt) => ({ value: opt, label: opt }))}
          />

          {allowOther && isOtherSelected && (
            <div className="other-input-wrap">
              <Input
                label="Other"
                hideLabel
                type="text"
                className="field-input"
                placeholder={field.otherLabel || "Please specify"}
                value={otherValue}
                maxLength={field.maxOtherLength || 40}
                onChange={(e) => handleOtherChange(e.target.value)}
                autoFocus
              />
              {field.maxOtherLength && (
                <div className="field-count">
                  {otherValue.length}/{field.maxOtherLength}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {type === "multiselect" && (
        <div className="chip-row">
          {options.map((opt) => {
            const selected = Array.isArray(value) && value.includes(opt);
            return (
              <button
                type="button"
                key={opt}
                className={`chip ${selected ? "selected" : ""}`}
                onClick={() => onToggle(opt)}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {type === "slider" && (
        <div className="field-slider">
          <input
            type="range"
            className={`field-range ${error ? "has-error" : ""}`}
            min={field.min ?? 0}
            max={field.max ?? 100}
            value={value ?? field.default ?? 50}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <div className="field-slider-endpoints">
            <span>{field.sliderLabels?.[0] || "Low"}</span>
            <span>{field.sliderLabels?.[1] || "High"}</span>
          </div>
        </div>
      )}

      {maxLength && type !== "select" && (
        <div className="field-count">
          {(value || "").length}/{maxLength}
        </div>
      )}

      {field.helpText && <div className="field-help">{field.helpText}</div>}

      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

const HEX_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

function ColorField({ value, onChange, defaultValue = "#0b2b66" }) {
  const current = HEX_RE.test(value || "") ? value : defaultValue;
  const [hexDraft, setHexDraft] = useState(current.replace(/^#/, ""));

  useEffect(() => {
    setHexDraft(current.replace(/^#/, ""));
  }, [current]);

  const draftIsValid = HEX_RE.test(`#${hexDraft}`);

  const commitDraft = () => {
    const candidate = `#${hexDraft}`;
    if (HEX_RE.test(candidate)) {
      onChange(candidate);
    } else {
      setHexDraft(current.replace(/^#/, ""));
    }
  };

  return (
    <div className="field-color-row">
      <div className="field-color-swatch-wrap">
        <input
          type="color"
          className="field-color-swatch"
          value={current}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Pick colour"
        />
      </div>
      <div className={`field-hex-wrap ${draftIsValid ? "" : "invalid"}`}>
        <span className="field-hex-prefix">#</span>
        <input
          type="text"
          className="field-hex-input"
          value={hexDraft}
          maxLength={6}
          placeholder="0B2B66"
          onChange={(e) =>
            setHexDraft(e.target.value.replace(/[^0-9A-Fa-f]/g, ""))
          }
          onBlur={commitDraft}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          aria-label="Hex colour code"
        />
      </div>
    </div>
  );
}

function ServiceRepeater({
  group,
  services,
  errors = {},
  onUpdate,
  onAdd,
  onRemove,
}) {
  return (
    <div>
      {services.map((service, i) => (
        <div key={i} className="repeat-card">
          <div className="repeat-card-header">
            <div className="repeat-card-title">Service {i + 1}</div>
            {services.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="btn-remove"
              >
                Remove
              </button>
            )}
          </div>
          {group.fields.map((f) => {
            const key = f.key.toLowerCase();
            const errorKey = `service_${i}_${key}`;
            const fieldError = errors[errorKey];
            const placeholder =
              f.placeholder || `Enter ${f.label.toLowerCase()}`;
            return (
              <div key={f.key} style={{ marginTop: 10 }}>
                <label className="repeat-sub-label">
                  {f.label}{" "}
                  {f.required ? (
                    <span className="field-required">*</span>
                  ) : (
                    <span className="field-optional-badge">Optional</span>
                  )}
                </label>
                {f.type === "checkbox" ? (
                  <input
                    type="checkbox"
                    className="repeat-checkbox"
                    checked={!!service[key]}
                    onChange={(e) => onUpdate(i, key, e.target.checked)}
                  />
                ) : f.type === "textarea" ? (
                  <Textarea
                    label={f.label}
                    hideLabel
                    className={`field-textarea ${fieldError ? "has-error" : ""}`}
                    placeholder={placeholder}
                    value={service[key] || ""}
                    maxLength={f.maxLength}
                    onChange={(e) => onUpdate(i, key, e.target.value)}
                    rows={2}
                  />
                ) : (
                  <Input
                    label={f.label}
                    hideLabel
                    type="text"
                    className={`field-input ${fieldError ? "has-error" : ""}`}
                    placeholder={placeholder}
                    value={service[key] || ""}
                    maxLength={f.maxLength}
                    onChange={(e) => onUpdate(i, key, e.target.value)}
                  />
                )}
                {f.maxLength && (
                  <div className="field-count">
                    {(service[key] || "").length}/{f.maxLength}
                  </div>
                )}
                {fieldError && <div className="field-error">{fieldError}</div>}
              </div>
            );
          })}
        </div>
      ))}
      {services.length < group.max && (
        <button type="button" onClick={onAdd} className="btn-dashed">
          + Add service
        </button>
      )}
    </div>
  );
}

function CompetitorRepeater({ max, values, errors = {}, onUpdate }) {
  return (
    <div className="field-group">
      <label className="field-label">
        Top {max} competitors{" "}
        <span className="field-optional-badge">Optional</span>
      </label>
      {Array.from({ length: max }).map((_, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <Input
            label={`Competitor ${i + 1}`}
            hideLabel
            type="url"
            className={`field-input ${errors[`competitor_${i}`] ? "has-error" : ""}`}
            placeholder={`https://competitor${i + 1}.com`}
            value={values[i] || ""}
            onChange={(e) => onUpdate(i, e.target.value)}
          />
          {errors[`competitor_${i}`] && (
            <div className="field-error">{errors[`competitor_${i}`]}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function ContactFields({ contact, errors = {}, onChange, companyCountry }) {
  return (
    <div className="contact-block">
      <div className="brief-eyebrow">Contact details</div>
      <div className="contact-grid">
        <div className="field-group" style={{ marginBottom: 0 }}>
          <label className="field-label">
            Phone <span className="field-required">*</span>
          </label>
          <PhoneInput
            label="Phone"
            hideLabel
            className="phone-field"
            value={contact.phone}
            // Functional updates: picking a country fires onCountryChange and onValueChange
            // in the same event, so each must build on the latest state, not this render's copy.
            onValueChange={(e164) =>
              onChange((prev) => ({ ...prev, phone: e164 }))
            }
            country={contact.phoneCountry}
            onCountryChange={(iso) => {
              const entry = PHONE_COUNTRIES.find((c) => c.iso === iso);
              onChange((prev) => ({
                ...prev,
                phoneCountry: iso,
                phoneCountryCode: entry
                  ? `+${entry.dial}`
                  : prev.phoneCountryCode,
              }));
            }}
            defaultCountry="GB"
            preferredCountries={["GB", "IE", "US", "AU", "ZA"]}
            error={errors.contact_phone}
          />
        </div>
        <div className="field-group" style={{ marginBottom: 0 }}>
          <label className="field-label">
            Email <span className="field-required">*</span>
          </label>
          <Input
            label="Email"
            hideLabel
            type="email"
            className={`field-input ${errors.contact_email ? "has-error" : ""}`}
            placeholder="name@company.com"
            value={contact.email}
            onChange={(e) => onChange({ ...contact, email: e.target.value })}
          />
          {errors.contact_email && (
            <div className="field-error">{errors.contact_email}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function NavButtons({
  activeSection,
  total,
  onBack,
  onNext,
  onSubmit,
  saving,
  overBudget,
}) {
  const isLast = activeSection === total - 1;
  return (
    <div className="brief-footer-nav">
      <button
        type="button"
        onClick={onBack}
        disabled={activeSection === 0}
        className="btn btn-ghost"
      >
        Back
      </button>
      {isLast ? (
        <button
          type="button"
          onClick={onSubmit}
          disabled={saving || overBudget}
          title={overBudget ? "Over the 5000-character limit" : undefined}
          className="btn btn-primary"
        >
          {saving ? "Submitting…" : "Submit"}
        </button>
      ) : (
        <button type="button" onClick={onNext} className="btn btn-primary">
          Continue
        </button>
      )}
    </div>
  );
}
